import { graphqlDynamic } from "react-apollo-dynamic-query"
import gql from "graphql-tag"
import ComplianceTableWithControls from "."
import {
  complianceNumberConverter,
  getClassName,
} from "@swipesense/compliance-helpers"
import { push } from "react-router-redux"
import moment, { duration } from "moment"
import {
  DEFAULT_CHILD_ENTITIES,
  GRAPHQL_DATE_FORMAT,
  NR_MIN_OPPORTUNITIES,
  BSR_MIN_OPPORTUNITIES,
} from "../../helpers/sharedConstants"
import { connect } from "react-redux"
import {
  getEntityTypeMapping,
  getMatchFromRouter,
} from "../../helpers/sharedHelperFunctions"

const queryArguments = childEntityType => {
  switch (childEntityType) {
    case "locations":
      return "(patientArea: true)"
    case "users":
      return "(employedAfter: $startAt, employedBefore: $endAt)"
    default:
      return ""
  }
}

/**
 * Here, we dynamically generate our query. We need to do this because we can't
 * simply use variables to change field names. For example, take the following
 * two queries:
 *
 * { facility(id: 7) { departments { ... } } } # get all departments from a facility
 * { department(id: 15) { users { ... } } }    # get all users from a department
 *
 * These two queries not only have different variables (the entity ID), they also
 * use *different fields*. Because of this, we need to dynamically generate our query.
 *
 * Here's an example query produced by this function:
 *
 * {
 *   entity: facility(id: $entityId) {
 *     childEntities: departments {
 *       ...
 *     }
 *   }
 * }
 *
 * In this query, entityType is "facility" and childEntityType is "departments".
 */
const bsrAggregates = `
first: aggregates(shiftNumber: 1) {
  opportunityCount
  compliantOpportunityCount
  compliancePercentage
}
second: aggregates(shiftNumber: 2) {
  opportunityCount
  compliantOpportunityCount
  compliancePercentage
}
third: aggregates(shiftNumber: 3) {
  opportunityCount
  compliantOpportunityCount
  compliancePercentage
}
`

const entityQuery = (entityType, childEntityType, bsrCompliance) => `
  entity: ${entityType}(id: $entityId) {
    id
    name
    childEntities: ${childEntityType}${queryArguments(childEntityType)} {
      id
      name
      ${
        childEntityType === "users"
          ? "healthcareRole department { id name }"
          : ""
      }
      ${childEntityType === "locations" ? "unit { id name }" : ""}
      compliance: ${
        bsrCompliance ? "bsrCompliance" : "roundingCompliance"
      }(startAt: $startAt, endAt: $endAt) {
        timeslices {
          full {
            aggregates {
              overall {
                compliancePercentage
                ${
                  bsrCompliance
                    ? "compliantOpportunityCount"
                    : "compliantRoundCount"
                }
                ${bsrCompliance ? "opportunityCount" : "roundCount"}
              }
            }
            ${bsrCompliance ? bsrAggregates : ""}
          }
        }
      },
      prevCompliance: ${
        bsrCompliance ? "bsrCompliance" : "roundingCompliance"
      }(startAt: $prevStartAt, endAt: $prevEndAt) {
        timeslices {
          full {
            aggregates {
              overall {
                compliancePercentage
                ${
                  bsrCompliance
                    ? "compliantOpportunityCount"
                    : "compliantRoundCount"
                }
                ${bsrCompliance ? "opportunityCount" : "roundCount"}
              }
            }
            ${bsrCompliance ? bsrAggregates : ""}
          }
        }
      }
    }
  }
`

export const generateTableDataQuery = ({
  parentEntityType,
  entityType,
  childEntityType,
  bsrCompliance,
}) => {
  if (parentEntityType) {
    return gql`
      query TableDataNested($parentEntityId: ID!, $entityId: ID!, $startAt: String!, $endAt: String!, $prevStartAt: String!, $prevEndAt: String!) {
        parentEntity: ${parentEntityType}(id: $parentEntityId) {
          id
          ${entityQuery(entityType, childEntityType, bsrCompliance)}
        }
      }
    `
  } else {
    return gql`
      query TableData($entityId: ID!, $startAt: String!, $endAt: String!, $prevStartAt: String!, $prevEndAt: String!) {
        ${entityQuery(entityType, childEntityType, bsrCompliance)}
      }
    `
  }
}

const subName = childEntityType => {
  switch (childEntityType) {
    case "users":
      return user => user.healthcareRole
    case "locations":
      return location => location.unit.name
    default:
      return undefined
  }
}

const ComplianceTableWithControlsWithApollo = graphqlDynamic(
  generateTableDataQuery,
  {
    options: ({ parentEntityId, entityId, startDate, endDate }) => ({
      variables: {
        entityId,
        parentEntityId,
        startAt: startDate.format(GRAPHQL_DATE_FORMAT),
        endAt: endDate.format(GRAPHQL_DATE_FORMAT),
        prevStartAt: getPreviousPeriod(startDate, endDate).startDate.format(
          GRAPHQL_DATE_FORMAT
        ),
        prevEndAt: getPreviousPeriod(startDate, endDate).endDate.format(
          GRAPHQL_DATE_FORMAT
        ),
      },
    }),
    props: ({ ownProps, data: { error, loading, parentEntity, entity } }) => {
      if (error) {
        throw error
      }
      if (!loading) {
        entity = entity || (parentEntity && parentEntity.entity)

        if (!entity) {
          throw Error("404")
        }

        const min_opportunities = ownProps.bsrCompliance
          ? BSR_MIN_OPPORTUNITIES
          : NR_MIN_OPPORTUNITIES

        return {
          loading,
          simpleDisplay: ownProps.simpleDisplay,
          entityName: entity.name,
          duration: duration,
          subName: subName(ownProps.childEntityType),
          data: entity.childEntities
            .map(item => {
              const aggs = item.compliance.timeslices.full.aggregates.overall
              const aggsFirst = item.compliance.timeslices.full.first
              const aggsSecond = item.compliance.timeslices.full.second
              const aggsThird = item.compliance.timeslices.full.third
              const prevAggs =
                item.prevCompliance.timeslices.full.aggregates.overall
              const prevAggsFirst = item.prevCompliance.timeslices.full.first
              const prevAggsSecond = item.prevCompliance.timeslices.full.second
              const prevAggsThird = item.prevCompliance.timeslices.full.third
              if (ownProps.bsrCompliance) {
                return {
                  ...item,
                  compliance: aggs.compliancePercentage,
                  complianceFirst: aggsFirst.compliancePercentage,
                  complianceSecond: aggsSecond.compliancePercentage,
                  complianceThird: aggsThird.compliancePercentage,
                  change:
                    aggs.compliancePercentage - prevAggs.compliancePercentage,
                  changeFirst:
                    aggsFirst.compliancePercentage -
                    prevAggsFirst.compliancePercentage,
                  changeSecond:
                    aggsSecond.compliancePercentage -
                    prevAggsSecond.compliancePercentage,
                  changeThird:
                    aggsThird.compliancePercentage -
                    prevAggsThird.compliancePercentage,
                  opportunities: aggs.opportunityCount,
                  opportunitiesFirst: aggsFirst.opportunityCount,
                  opportunitiesSecond: aggsSecond.opportunityCount,
                  opportunitiesThird: aggsThird.opportunityCount,
                  bucket: getClassName(
                    complianceNumberConverter(
                      aggs.compliancePercentage,
                      aggs.opportunityCount,
                      min_opportunities
                    )
                  ),
                }
              }
              return {
                ...item,
                compliance: aggs.compliancePercentage,
                change:
                  aggs.compliancePercentage - prevAggs.compliancePercentage,
                opportunities: aggs.roundCount,
                bucket: getClassName(
                  complianceNumberConverter(
                    aggs.compliancePercentage,
                    aggs.roundCount,
                    min_opportunities
                  )
                ),
              }
            })
            .filter(item => item.opportunities > 0),
        } // Only show entities with data for the selected time period.
      } else {
        return { loading, error, data: [] }
      }
    },
  }
)(ComplianceTableWithControls)

/*
Returns the previous start and end date of the same duration. Note this will
will be off by a day if the periods are months (e.g. for 12/1 - 12/31, the
previous period would calculate to 10/31 - 11/30)
*/
const getPreviousPeriod = (date1, date2) => {
  const endDate = moment(date1).subtract(1, "day")
  const diffInSecs = moment(date2).diff(moment(date1))
  return { startDate: moment(endDate).subtract(diffInSecs), endDate: endDate }
}

const mapStateToProps = ({
  router,
  dateRange: { startDate, endDate },
  bsrCompliance: { bsrCompliance },
}) => {
  const match = getMatchFromRouter(router)
  const locationState = router.location && router.location.state
  return {
    startDate: moment(startDate),
    endDate: moment(endDate),
    parentEntityType:
      match.params.parentEntityType &&
      getEntityTypeMapping(match.params.parentEntityType).singularField,
    parentEntityId: match.params.parentEntityId,
    entityType: getEntityTypeMapping(match.params.entityType).singularField,
    entityId: match.params.entityId,
    childEntityType: getEntityTypeMapping(
      match.params.childEntityType ||
        DEFAULT_CHILD_ENTITIES[match.params.entityType]
    ).pluralField,
    onlyComplianceData: locationState && locationState.onlyComplianceData,
    dataSort: locationState && locationState.dataSort,
    bsrCompliance: bsrCompliance,
  }
}

const mapDispatchToProps = dispatch => ({
  updatePath: path => {
    dispatch(push(path))
  },
})

const ComplianceTableWithControlsWithData = connect(
  mapStateToProps,
  mapDispatchToProps
)(ComplianceTableWithControlsWithApollo)

export default ComplianceTableWithControlsWithData
